/**
 * Родной протокол глубоких ссылок Phantom — подключение с возвратом в приложение.
 *
 * Зачем он вообще нужен. У Phantom нет WalletConnect, и это не пробел в
 * настройках, а решение кошелька: круг «нажал в приложении → подтвердил в
 * кошельке → вернулся в приложение», который для MetaMask даёт relay
 * WalletConnect, для Solana даёт только этот протокол. Всё остальное, что мы
 * умели до сих пор, — увести человека во встроенный браузер Phantom. Там он
 * оказывается в чужом WebView, где не авторизован, и обратно возвращается
 * руками.
 *
 * Как устроено (docs.phantom.com, «Deeplinks / Provider Methods / Connect»).
 * Приложение создаёт эфемерную пару ключей x25519 и отдаёт Phantom только
 * открытый ключ. Phantom отвечает не в тело запроса — тела нет, это переход
 * между приложениями, — а параметрами возвратного адреса, зашифровав ответ
 * общим секретом (nacl.box: x25519 + xsalsa20-poly1305). Расшифровать ответ
 * может только тот, у кого закрытый ключ пары, то есть эта вкладка.
 *
 * Что здесь хранится и чем это НЕ является. `wt_phantom_handshake` и
 * `wt_phantom_session` держат эфемерный ключ шифрования переписки с Phantom и
 * непрозрачный идентификатор сессии. Это не ключ кошелька и не право подписи:
 * укравший их не может ни перевести средства, ни подписать транзакцию — любая
 * подпись по-прежнему требует подтверждения в самом Phantom. Ключ живёт до
 * отключения кошелька, потому что тем же общим секретом шифруются последующие
 * запросы на подпись.
 *
 * Границы. Возврат работает там, где возвращаемый адрес открывает наше
 * приложение: на Android — установленное PWA (если подтверждено
 * assetlinks.json) либо браузер по умолчанию; в обычном мобильном браузере —
 * всегда. На iOS универсальная ссылка в установленное PWA не возвращает
 * (ограничение системы, не наше) — там человек вернётся в Safari.
 */

import bs58 from 'bs58';
import nacl from 'tweetnacl';

const PHANTOM_CONNECT_URL = 'https://phantom.app/ul/v1/connect';
const HANDSHAKE_KEY = 'wt_phantom_handshake';
const SESSION_KEY = 'wt_phantom_session';

/** Метка возвратного адреса: по ней страница понимает, что вернулась из Phantom. */
export const PHANTOM_RETURN_PARAM = 'wt_sol';
export const PHANTOM_RETURN_VALUE = 'phantom';

/** Рукопожатие живёт ровно столько, сколько человек может провести в кошельке. */
const HANDSHAKE_TTL_MS = 15 * 60 * 1000;

interface StoredHandshake {
  dappPublicKey: string;
  dappSecretKey: string;
  createdAt: number;
}

export interface PhantomSession {
  address: string;
  /** Непрозрачный идентификатор сессии — нужен для будущих запросов на подпись. */
  session: string;
  phantomPublicKey: string;
  dappSecretKey: string;
}

export type PhantomConnectOutcome =
  | { status: 'connected'; session: PhantomSession }
  | { status: 'rejected' }
  | { status: 'error'; detail: string };

function readJson<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

function writeJson(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* приватный режим или переполненное хранилище — переход всё равно состоится,
       но ответ Phantom расшифровать будет нечем; это видно как ошибка на возврате */
  }
}

function drop(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}

/** Адрес, на который Phantom вернёт человека вместе с ответом. */
function redirectLink(): string {
  const { origin, pathname } = window.location;
  return `${origin}${pathname}?${PHANTOM_RETURN_PARAM}=${PHANTOM_RETURN_VALUE}`;
}

/**
 * Уйти в Phantom за подключением. Возвращения из функции не будет — начинается
 * переход в другое приложение.
 */
export function startPhantomConnect(): void {
  if (typeof window === 'undefined') return;

  const keyPair = nacl.box.keyPair();
  const handshake: StoredHandshake = {
    dappPublicKey: bs58.encode(keyPair.publicKey),
    dappSecretKey: bs58.encode(keyPair.secretKey),
    createdAt: Date.now(),
  };
  writeJson(HANDSHAKE_KEY, handshake);

  const params = new URLSearchParams({
    app_url: window.location.origin,
    dapp_encryption_public_key: handshake.dappPublicKey,
    redirect_link: redirectLink(),
    cluster: 'mainnet-beta',
  });
  window.location.href = `${PHANTOM_CONNECT_URL}?${params.toString()}`;
}

/** Вернулись ли мы из Phantom: в адресе метка или его собственные параметры. */
export function hasPhantomResponse(): boolean {
  if (typeof window === 'undefined') return false;
  const params = new URLSearchParams(window.location.search);
  return (
    params.get(PHANTOM_RETURN_PARAM) === PHANTOM_RETURN_VALUE ||
    params.has('phantom_encryption_public_key') ||
    params.has('errorCode')
  );
}

/**
 * Разобрать ответ Phantom из адресной строки.
 *
 * Отказ человека (errorCode 4001) отделён от поломки намеренно: «вы отказались
 * в кошельке» и «ответ не расшифровался» требуют от человека разного, а
 * выглядят одинаково — возвратом на ту же страницу без кошелька.
 */
export function readPhantomConnectResponse(): PhantomConnectOutcome | null {
  if (typeof window === 'undefined' || !hasPhantomResponse()) return null;

  const params = new URLSearchParams(window.location.search);

  const errorCode = params.get('errorCode');
  if (errorCode) {
    drop(HANDSHAKE_KEY);
    // 4001 — «пользователь отклонил запрос», общий для всех методов Phantom.
    if (errorCode === '4001') return { status: 'rejected' };
    return { status: 'error', detail: `${errorCode} ${params.get('errorMessage') || ''}`.trim() };
  }

  const phantomPublicKey = params.get('phantom_encryption_public_key');
  const nonce = params.get('nonce');
  const data = params.get('data');
  if (!phantomPublicKey || !nonce || !data) {
    // Метка есть, ответа нет: человек вернулся сам, ничего не подтвердив.
    return null;
  }

  const handshake = readJson<StoredHandshake>(HANDSHAKE_KEY);
  if (!handshake) {
    return { status: 'error', detail: 'handshake_missing' };
  }
  if (Date.now() - handshake.createdAt > HANDSHAKE_TTL_MS) {
    drop(HANDSHAKE_KEY);
    return { status: 'error', detail: 'handshake_expired' };
  }

  try {
    const shared = nacl.box.before(bs58.decode(phantomPublicKey), bs58.decode(handshake.dappSecretKey));
    const opened = nacl.box.open.after(bs58.decode(data), bs58.decode(nonce), shared);
    if (!opened) {
      drop(HANDSHAKE_KEY);
      return { status: 'error', detail: 'decrypt_failed' };
    }
    const payload = JSON.parse(new TextDecoder().decode(opened)) as {
      public_key?: string;
      session?: string;
    };
    if (!payload.public_key || !payload.session) {
      drop(HANDSHAKE_KEY);
      return { status: 'error', detail: 'malformed_payload' };
    }

    const session: PhantomSession = {
      address: payload.public_key,
      session: payload.session,
      phantomPublicKey,
      dappSecretKey: handshake.dappSecretKey,
    };
    writeJson(SESSION_KEY, session);
    drop(HANDSHAKE_KEY);
    return { status: 'connected', session };
  } catch (err: any) {
    drop(HANDSHAKE_KEY);
    return { status: 'error', detail: String(err?.message || err) };
  }
}

/** Убрать ответ Phantom из адреса: в истории и в referrer ему делать нечего. */
export function stripPhantomResponseFromUrl(): void {
  if (typeof window === 'undefined' || !window.history?.replaceState) return;
  try {
    const url = new URL(window.location.href);
    [
      PHANTOM_RETURN_PARAM,
      'phantom_encryption_public_key',
      'nonce',
      'data',
      'errorCode',
      'errorMessage',
    ].forEach((param) => url.searchParams.delete(param));
    window.history.replaceState({}, '', url.toString());
  } catch {
    /* адрес не разобрался — оставляем как есть */
  }
}

/** Сохранённая сессия Phantom: подключение переживает перезагрузку страницы. */
export function storedPhantomSession(): PhantomSession | null {
  const session = readJson<PhantomSession>(SESSION_KEY);
  return session?.address ? session : null;
}

export function clearPhantomSession(): void {
  drop(SESSION_KEY);
  drop(HANDSHAKE_KEY);
}
